
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { environment } from "environments/environment";
import { Observable, throwError, of } from "rxjs";
import { catchError, switchMap, map, shareReplay } from "rxjs/operators";
import { setting } from '../setting';
import { AuthorizatedGuard } from "app/pages/com.usblick.common/guard/authorizated.guard";
import { RestService } from 'app/shared/rest.service';
import { NotificationService } from './app/shared/notifications/notification.service';

@Injectable()
export class HttpConfigInterceptor implements HttpInterceptor {

    private isRedirecting = false;
    private lastVerification = 0;
    private pendingVerification: Observable<boolean> | null = null;
    private readonly CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutos

    // true solo después de que verifyToken() confirmó una sesión válida al
    // menos una vez en este load de la app. Distingue "nunca hubo sesión"
    // (home sin login, guest antes de buscar — no expulsar) de "había sesión
    // y dejó de ser válida" (expiró de verdad — ahí sí corresponde expulsar).
    private hasConfirmedSession = false;

    // Endpoints que no requieren sesión válida: datos públicos que un guest
    // necesita antes de tener agente asignado (autocomplete, contenido de
    // home) y el propio endpoint que crea la sesión de guest. Sin esto, la
    // verificación dispara en la primera de estas llamadas y expulsa al
    // guest antes de que llegue a buscar.
    // TODO: completar la lista con el resto del inventario de llamadas
    // pre-búsqueda (día 1 del plan B2C) — todavía no está completo.
    private readonly SESSION_EXEMPT_PATTERNS: string[] = [
        '/user/current?from=booking',
        '/guest', // API\GuestController@store — alta de invitado B2C
        '/utilities/searchlocations',
        'getClientsSettingAppBooking',
    ];

    constructor(
        private router: Router,
        private authorizatedGuard: AuthorizatedGuard,
        private notificationService: NotificationService,
        private _restService: RestService
    ) {}

    intercept(
        req: HttpRequest<any>,
        next: HttpHandler): Observable<HttpEvent<any>>
    {
        // La sesión viaja en la cookie HttpOnly auth_token; el browser la adjunta
        // solo si el request pide credentials. Acotado a la API de booking para no
        // romper llamadas a otros orígenes con CORS wildcard (igual que en AuthAPP).
        if (req.url.startsWith(setting.apiBookingUrl)) {
            req = req.clone({ withCredentials: true });
        }

        // Excluir los endpoints que no requieren sesión (ver SESSION_EXEMPT_PATTERNS)
        if (this.isSessionExempt(req.url)) {
            return next.handle(this.addHeaders(req));
        }

        // No interceptor para assets
        if (req.url.includes('.svg') || req.url.includes('.png') || req.url.includes('.jpg')) {
            return next.handle(req);
        }

        const now = Date.now();
        const needsVerification = (now - this.lastVerification) >= this.CHECK_INTERVAL;

        // ✅ Si no necesita verificación (menos de 5 minutos desde la última), continuar directamente
        if (!needsVerification) {
            // console.log('⏭️ Verificación no necesaria (última hace', Math.round((now - this.lastVerification) / 1000), 'segundos)');
            return next.handle(this.addHeaders(req));
        }



        return this.verifyToken().pipe(
            switchMap(isValid => {
                // Se throttlea acá SIEMPRE, sea cual sea el resultado — si no,
                // mientras no hay sesión, cada request de la página vuelve a
                // disparar needsVerification y encadena un verifyToken() por
                // cada una (countries, currencies, locationAgency, etc.),
                // en serie, antes de dejar pasar nada.
                this.lastVerification = Date.now();

                if (isValid) {
                    this.hasConfirmedSession = true;
                    // console.log('✅ Token válido, próxima verificación en 5 minutos');
                    return next.handle(this.addHeaders(req));
                }

                if (this.hasConfirmedSession) {
                    // Hubo sesión válida y dejó de estarlo: expiró de verdad.
                    this.handleInvalidToken();
                    return throwError(() => new Error('Token inválido'));
                }

                // Nunca se confirmó sesión en este load — no es una expiración,
                // es simplemente que todavía no hay sesión (home sin login,
                // guest antes de buscar). No expulsar; las rutas que sí la
                // requieren están protegidas por AuthorizatedGuard.
                return next.handle(this.addHeaders(req));
            })
        );
    }

    private isSessionExempt(url: string): boolean {
        return this.SESSION_EXEMPT_PATTERNS.some(pattern => url.includes(pattern));
    }

    private verifyToken(): Observable<boolean> {
        // Si ya hay una verificación en curso, reutilizarla
        if (this.pendingVerification) {
            return this.pendingVerification;
        }

        this.pendingVerification = this._restService.doGet("app", "user/current?from=booking").pipe(
            map((response: any) => {
                const isValid = response.res === 1 || response?.user?.id;
                return isValid;
            }),
            catchError(() => of(false)),
            map(result => {
                this.pendingVerification = null;
                return result;
            }),
            // doGet() returns a cold Observable — without shareReplay, every concurrent
            // subscriber to this "cached" reference would trigger its own real HTTP call,
            // defeating the dedup this method is meant to provide.
            shareReplay(1)
        );

        return this.pendingVerification;
    }

    private handleInvalidToken(): void {
        if (this.isRedirecting) return;

        this.isRedirecting = true;
        this.notificationService.warning('Sesión expirada, vuelva a loguearse');

        setTimeout(() => {
            this.authorizatedGuard.letsRedirect();
            this.isRedirecting = false;
        }, 2000);
    }

    private addHeaders(req: HttpRequest<any>): HttpRequest<any> {
        let cloned = req;

        if(!req.headers.has('Content-Type')){
            cloned = cloned.clone({headers: req.headers.set('Content-Type', 'application/json') });
        }
        if(!req.headers.has('Accept')){
            cloned = cloned.clone({headers: req.headers.set('Accept', 'application/json') });
        }
        if(!req.headers.has('client-id')){
            cloned = cloned.clone({headers: req.headers.set('client-id', environment.client_id) });
        }
        if(!req.headers.has('client-secret')){
            cloned = cloned.clone({headers: req.headers.set('client-secret', environment.client_secret) });
        }
        if(!req.headers.has('ApiUser')){
            cloned = cloned.clone({headers: req.headers.set('ApiUser', setting.ApiUser) });
        }
        if(!req.headers.has('ApiKey')){
            cloned = cloned.clone({headers: req.headers.set('ApiKey', setting.ApiKey) });
        }
        if(!req.headers.has('x-api-user')){
              cloned = cloned.clone({headers: cloned.headers.set('x-api-user', setting.booking_api_user)})
        }
        if(!req.headers.has('x-api-key')){
              cloned = cloned.clone({headers: cloned.headers.set('x-api-key', setting.booking_api_key)})
        }
        // Ya no se arma Authorization a mano: la sesión viaja en la cookie HttpOnly, vía withCredentials.

        return cloned;
    }
}
